import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { isTransient } from '~/api/client';
import { BrandGlyph } from '~/ui/BrandMark';
import { Button } from '~/ui/Button';

/** A screen's code failed to download (a network blip, or a build replaced by a deploy). */
const isChunkError = (e: unknown) =>
  e instanceof Error &&
  /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch/i.test(
    e.message,
  );

/** Tries again after 2 s, 4 s, 8 s … then every 30 s. */
const delayFor = (attempt: number) => Math.min(30_000, 2_000 * 2 ** attempt);

/**
 * What a page shows when it could not open, in place of the router's own error
 * page. An outage (no connection, the server restarting during a deploy, a
 * screen's code that did not download) is waited out: "Reconnecting", tried
 * again by itself, at once when the network returns or the window comes back,
 * and with Try now. Anything else is a plain message with Reload. The desktop
 * app shows this site, so the same screen covers it.
 */
export function AppError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const outage = isTransient(error) || isChunkError(error) || !navigator.onLine;
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    if (isChunkError(error)) {
      window.location.reload();
      return;
    }
    setAttempt((a) => a + 1);
    reset();
    void router.invalidate();
  }, [error, reset, router]);

  useEffect(() => {
    if (!outage) return;
    const timer = setTimeout(retry, delayFor(attempt));
    const now = () => retry();
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry();
    };
    window.addEventListener('online', now);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', now);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [outage, attempt, retry]);

  return (
    <main
      data-app-error={outage ? 'outage' : 'error'}
      className="flex min-h-dvh items-center justify-center bg-paper px-4 py-10 text-ink"
    >
      <div className="w-full max-w-sm text-center">
        <span
          aria-hidden
          className="mx-auto grid size-12 place-items-center rounded-control bg-accent text-on-accent"
        >
          <BrandGlyph className="size-full" />
        </span>
        {outage ? (
          <>
            <h1 className="mt-6 text-xl font-semibold">Reconnecting to 11Deeds…</h1>
            <p className="mt-2 text-sm text-ink-2" role="status">
              {navigator.onLine
                ? 'The server is restarting or the connection dropped. This page tries again by itself.'
                : 'This device is offline. 11Deeds opens again as soon as the connection is back.'}
            </p>
            <Button
              className="mt-6"
              variant="secondary"
              icon={<RefreshCw size={16} aria-hidden />}
              onClick={retry}
            >
              Try now
            </Button>
          </>
        ) : (
          <>
            <TriangleAlert size={20} aria-hidden className="mx-auto mt-6 text-late" />
            <h1 className="mt-2 text-xl font-semibold">This page could not open.</h1>
            <p className="mt-2 text-sm text-ink-2">
              Reload to try again. If it happens again, tell your manager what you clicked.
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Button
                icon={<RefreshCw size={16} aria-hidden />}
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
              <Button variant="secondary" onClick={() => window.location.assign('/dashboard')}>
                Go to the dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
